import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { BRAND } from '../shared/theme/paletteDark';
import { useScrollMemory } from '../shared/useScrollMemory';
import { HANDLE_W, SIDEBAR_GROUND, SIDEBAR_SHADOW_W, SidebarTop, sidebarShadow } from './Frame';

/**
 * A scaling sidebar pane, shared by Explore's filters and My Schedule's panel
 * below 900px (25%, on the right). Draggable between 18% and 50%.
 *
 * The contents scale rather than reflow. Three topic columns stay three
 * columns and the ten day buttons stay on one row at every width — only the
 * size changes.
 *
 * That is done with `zoom` rather than `transform: scale()`. `zoom`
 * participates in layout, so the scroll height stays correct, and because the
 * zoom factor is exactly (actual width / NATURAL_WIDTH), the content always
 * resolves to NATURAL_WIDTH CSS pixels of space no matter how wide the pane
 * really is. That is what makes the layout constant.
 */

export const MIN_FRACTION = 0.18;
export const MAX_FRACTION = 0.5;

/** The width the sidebar's contents are designed against. */
const NATURAL_WIDTH = 500;
const MIN_ZOOM = 0.74;
const MAX_ZOOM = 1.45;

/** The contents' side padding, before zoom. The top block matches it after. */
const INNER_X = 20;

const Column = styled('div', { shouldForwardProp: (p) => p !== '$flat' })<{ $flat: boolean }>(({ $flat }) => ({
  position: 'relative',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  // Flat: a full-height column from 900px up. Otherwise a rounded panel
  // under the header.
  ...($flat ? { background: SIDEBAR_GROUND } : { borderRadius: 14, background: '#292420' }),
  // A width set for it (the right sidebar opening into the callout beside it,
  // a double-click back to the default) eases, in step with the right sidebar.
  // A drag does not: the edge stays under the pointer.
  transition: 'flex-basis 320ms cubic-bezier(0.22, 1, 0.36, 1)',
  '&[data-dragging="true"]': { transition: 'none' },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
}));

const Pane = styled('div')({
  position: 'relative',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  overscrollBehavior: 'contain',
});

const Inner = styled('div')({
  padding: `18px ${INNER_X}px 40px`,
});

const Handle = styled('div')({
  position: 'relative',
  width: HANDLE_W,
  flexShrink: 0,
  transition: 'width 320ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms ease',
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  cursor: 'col-resize',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  touchAction: 'none',
  '&::before': {
    content: '""',
    width: 2,
    height: 46,
    borderRadius: 2,
    background: 'rgba(243,238,231,0.22)',
    transition: 'background 120ms ease, height 120ms ease',
  },
  '&:hover::before, &[data-dragging="true"]::before': {
    background: 'rgba(243,238,231,0.65)',
    height: 78,
  },
  // Against a flat sidebar the seam is edge enough; the grip shows on approach.
  '&[data-flat="true"]:not(:hover):not([data-dragging="true"])::before': {
    background: 'transparent',
  },
  // The flat sidebar's shadow on the page, which begins at the handle. Only
  // the filters are flat and on the left; it goes with the handle when they close.
  '&[data-flat="true"]::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: SIDEBAR_SHADOW_W,
    background: sidebarShadow('right'),
    pointerEvents: 'none',
  },
});

interface Props {
  children: ReactNode;
  /** Remembers this pane's scroll position across unmounts, under this key. */
  scrollKey?: string;
  /** The share of the page it opens at, and returns to on a double-click. The golden ratio by default. */
  defaultFraction?: number;
  /** Reports the fraction so the sibling pane can size itself. */
  onFractionChange?: (fraction: number) => void;
  /**
   * Held by the caller rather than here, which then answers the drag with
   * `onResize` and the double-click with `onReset`: Explore's filters, whose
   * width has to give way to the right sidebar.
   */
  fraction?: number;
  onResize?: (fraction: number) => void;
  onReset?: () => void;
  /** Below MAX_FRACTION when something beside it needs the room. The drag stops here. */
  maxFraction?: number;
  /** Which edge of the page it sits on. The handle goes on the other. */
  side?: 'left' | 'right';
  /** Full height on the sidebar ground, from 900px up. */
  flat?: boolean;
  /**
   * Held above the scrolling contents and outside their zoom: the lockup
   * keeps its own size rather than shrinking from 500px with the filters.
   */
  top?: ReactNode;
  /** For the handle: what is being resized. */
  label?: string;
  /**
   * Stepped aside or closed, but still drawn: it eases to nothing rather than
   * vanishing, as the right sidebar beside it opens into the callout.
   */
  collapsed?: boolean;
}

export default function ResizableSidebar({
  children,
  scrollKey,
  defaultFraction = 0.382,
  onFractionChange,
  fraction: held,
  onResize,
  onReset,
  maxFraction = MAX_FRACTION,
  side = 'left',
  flat = false,
  top,
  label = 'Resize the filter sidebar',
  collapsed = false,
}: Props) {
  const { ref: attachScroll, onScroll } = useScrollMemory(scrollKey ?? '', Boolean(scrollKey));
  const [own, setOwn] = useState(defaultFraction);
  const fraction = held ?? own;
  const ceiling = Math.max(MIN_FRACTION, Math.min(MAX_FRACTION, maxFraction));

  /** Read by the drag and the arrow keys, which outlive the render they began in. */
  const latest = useRef({ fraction, ceiling, onResize });
  latest.current = { fraction, ceiling, onResize };
  const setFraction = useCallback((next: number) => {
    const { ceiling: top, onResize: report } = latest.current;
    const clamped = Math.min(top, Math.max(MIN_FRACTION, next));
    if (report) report(clamped);
    else setOwn(clamped);
  }, []);
  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(false);
  const paneRef = useRef<HTMLDivElement | null>(null);

  /**
   * The pane is measured here and remembered by useScrollMemory, so both need
   * the node. Memoized rather than inline: an inline ref callback is a new
   * function every render, which would make React detach and re-attach the node
   * constantly — and every attach re-applies the remembered scroll position,
   * fighting the user as they scroll.
   */
  const setPane = useCallback(
    (node: HTMLDivElement | null) => {
      paneRef.current = node;
      attachScroll(node);
    },
    [attachScroll],
  );

  // Keep the zoom factor tied to the pane's real pixel width.
  useEffect(() => {
    const node = paneRef.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      if (width > 0) {
        setZoom(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, width / NATURAL_WIDTH)));
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => onFractionChange?.(fraction), [fraction, onFractionChange]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const handle = e.currentTarget;
      handle.setPointerCapture(e.pointerId);
      setDragging(true);

      const container = handle.parentElement;
      if (!container) return;

      const move = (ev: PointerEvent) => {
        const rect = container.getBoundingClientRect();
        const reach = side === 'left' ? ev.clientX - rect.left : rect.right - ev.clientX;
        setFraction(reach / rect.width);
      };
      const up = () => {
        setDragging(false);
        handle.removeEventListener('pointermove', move);
        handle.removeEventListener('pointerup', up);
      };

      handle.addEventListener('pointermove', move);
      handle.addEventListener('pointerup', up);
    },
    [side, setFraction],
  );

  // Keyboard resize, so the control isn't pointer-only. The arrow pointing
  // away from the sidebar's own edge widens it.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const step = (e.key === 'ArrowRight') === (side === 'left') ? 0.02 : -0.02;
      setFraction(latest.current.fraction + step);
    },
    [side, setFraction],
  );

  const column = (
    <Column
      $flat={flat}
      data-dragging={dragging}
      style={{ flex: `0 0 ${collapsed ? 0 : fraction * 100}%` }}
      inert={collapsed}
      aria-hidden={collapsed || undefined}
    >
      {top && <SidebarTop style={{ padding: `0 ${INNER_X * zoom}px` }}>{top}</SidebarTop>}
      <Pane ref={setPane} onScroll={onScroll}>
        {/* `zoom` scales layout as well as paint, unlike transform. */}
        <Inner style={{ zoom }}>{children}</Inner>
      </Pane>
    </Column>
  );

  const handle = (
    <Handle
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={Math.round(fraction * 100)}
      aria-valuemin={MIN_FRACTION * 100}
      aria-valuemax={Math.round(ceiling * 100)}
      tabIndex={collapsed ? -1 : 0}
      data-dragging={dragging}
      data-flat={flat}
      data-own-swipe
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onDoubleClick={() => (onReset ? onReset() : setOwn(defaultFraction))}
      title="Drag to resize · double-click to reset"
      style={{ color: BRAND.parchment, ...(collapsed && { width: 0, opacity: 0, pointerEvents: 'none' as const }) }}
    />
  );

  return side === 'left' ? (
    <>
      {column}
      {handle}
    </>
  ) : (
    <>
      {handle}
      {column}
    </>
  );
}
